import React, {
  useCallback,
  useMemo,
  useContext,
  useRef,
  useState,
  useEffect,
} from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry } from "ag-grid-community";
import AppContext from "../Store/AppContext";
import { getUrl } from "../Network/urls";
import { get } from "../Network/requests";
import Tooltip from "@mui/material/Tooltip";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { ClientSideRowModelModule } from "ag-grid-community";
import { TooltipModule } from "ag-grid-community";
import NotSubscribedMessageComponent from "./UI/NotSubscribedMessageComponent";
import CircularProgress from "@mui/material/CircularProgress";
import toast from "react-hot-toast";
import "../Assets/css/euRecordKeepingAnalytics.css";
import {
  PaginationModule,
  ContextMenuModule,
  ServerSideRowModelModule,
  TextFilterModule,
  NumberFilterModule,
  ValidationModule,
  ColumnApiModule,
  ScrollApiModule,
  ColumnMenuModule,
  SetFilterModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  IntegratedChartsModule,
  PivotModule,
  RowGroupingPanelModule,
  ColumnAutoSizeModule,
} from "ag-grid-enterprise";
import { CsvExportModule } from "ag-grid-community";
import GridRowDetailsExpand from "./UI/GridRowDetailsExpand";
import { TbArrowsMinimize } from "react-icons/tb";
import { LuExpand } from "react-icons/lu";
import DownloadRefreshFullscreenGridActions from "./UI/GridGroupIcons";
import GridGroupIcons from "./UI/GridGroupIcons";
import CustomDatepicker from "./UI/CustomDatepicker";
import GridDataNotFound from "./UI/GridDataNotFound";
import DynamicTabSwitcher from "./UI/DynamicTabSwitcher";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  FormControl,
} from "@mui/material";
import { tokenExpire } from "../Utils";
import { useNavigate } from "react-router-dom";
import { BasicKPICalculation } from "./UI/KpiCalculationModal";
import { FaInfoCircle } from "react-icons/fa";
import IconButton from "@mui/material/IconButton";
// Register AG Grid Enterprise Modules
ModuleRegistry.registerModules([
  PaginationModule,
  ContextMenuModule,
  ServerSideRowModelModule,
  TextFilterModule,
  NumberFilterModule,
  ValidationModule,
  CsvExportModule,
  ColumnApiModule,
  ScrollApiModule,
  ColumnMenuModule,
  SetFilterModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  PivotModule,
  RowGroupingPanelModule,
  ColumnAutoSizeModule,
  TooltipModule,
]);

function EURecordKeepingAnnexIVOrdersComponent({
  productCode,
  regulationCode,
  isActive,
  dateRange,
  setDateRange,
  navigationDetails,
  sharedFilters,
  setSharedFilters,
}) {
  // Get client details from context
  const TAB_MAP = {
    rcd: "Records",
    err: "Errors",
  };

  const {
    clientDetails,
    handleAPIEncryptValue,
    handleDecryptResponse,
    handleEncryptValue,
  } = useContext(AppContext);
  const gridRef = useRef();
  const [isRegulationAccessible, setIsRegulationAccessible] = useState(false);
  const [gridApi, setGridApi] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [showContactSupport, setShowContactSupport] = useState(false);
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const [assetClassLoading, setAssetClassLoading] = useState(false);
  const [assetClassOpts, setAssetClassOpts] = useState(
    sharedFilters?.assetClassOpts ?? [],
  );
  // const [assetClass, setAssetClass] = useState(sharedFilters?.assetClass ?? 'all');
  // const setRegulatiionType = (val) => setSharedFilters?.((prev) => ({ ...prev, regulationType: val }));
  const setAssetClass = (val) =>
    setSharedFilters?.((prev) => ({ ...prev, [regulationCode]: val }));
  const assetClass = sharedFilters?.[regulationCode] ?? "all";

  // KPI Drill Down
  const drilldown = navigationDetails?.data?.drilldown || null;
  const initialTab = drilldown?.tab;
  const drilldownRecordType = drilldown?.recordType ?? null;
  console.log("sharedFilters:", sharedFilters);
  console.log("assetClass:", assetClass);
  const drilldownFilters = useMemo(() => {
    const filters = {};

    // KPI Drilldown
    if (drilldownRecordType) {
      if (
        typeof drilldownRecordType === "object" &&
        !Array.isArray(drilldownRecordType)
      ) {
        Object.assign(filters, drilldownRecordType);
      } else {
        const normalizeRecordType = (value) => {
          if (!value) return value;

          const normalized = String(value).trim();

          if (normalized.toLowerCase() === "orders") return "Order";
          if (normalized.toLowerCase() === "executions") return "Execution";

          return normalized;
        };

        const values = Array.isArray(drilldownRecordType)
          ? drilldownRecordType.map(normalizeRecordType)
          : [normalizeRecordType(drilldownRecordType)];

        filters.record_type_regx = {
          filterType: "set",
          values,
        };
      }
    }

    // Asset Class Drilldown
    if (assetClass && assetClass !== "all") {
      filters.product_regx = {
        filterType: "set",
        values: [assetClass],
      };
    }

    return Object.keys(filters).length ? filters : null;
  }, [drilldownRecordType, assetClass]);

  const drilldownFiltersRef = useRef(null);

  const [activeTab, setActiveTab] = useState(Object.keys(TAB_MAP)[0]);
  const activeTabRef = useRef(Object.keys(TAB_MAP)[0]);
  const [showTable, setShowTable] = useState(false);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    activeTabRef.current = tab;
  };

  // KPI States
  const emptyKpis = {
    totalOrds: 0,
    validOrds: 0,
    validPctOfTotal: 0,
    invalidOrds: 0,
    invalidPctOfTotal: 0,
    excludedOrds: 0,
    excludedPctOfTotal: 0,
    accuracyPct: 0,
    errorPct: 0,
  };

  const [kpiData, setKpiData] = useState(emptyKpis);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [activeBasicKpiKey, setActiveBasicKpiKey] = useState(null);

  const openBasicKpiModal = (key) => setActiveBasicKpiKey(key);
  const closeBasicKpiModal = () => setActiveBasicKpiKey(null);

  const [startDate, endDate] = dateRange;
  // Ref to hold the debounce timer for filter refresh
  const filterChangedTimeout = useRef(null);
  // Ref to store previous active state for asynchronous filters (first 4 columns)
  const prevAsyncActiveState = useRef({});
  const navigate = useNavigate();
  // URL endpoints from your configuration
  const filterUrl = getUrl("get-falsk-filter-values");
  const downloadUrl = getUrl("get-download-csv");

  // Helper functions for KPI calculations
  const fmt = (num) => Number(num).toLocaleString("en-US");
  const getRowByType = (rows, type) => rows.find((r) => r?.[0] === type) ?? [];
  const getValue = (row, idx) => Number(row?.[idx] ?? 0);

  // Fetch KPI data
  const fetchKpiData = async () => {
    if (!startDate || !endDate) return;

    setKpiLoading(true);
    try {
      const start = startDate.toLocaleDateString("fr-CA");
      const end = endDate
        ? new Date(
          new Date(endDate).setDate(endDate.getDate() + 1),
        ).toLocaleDateString("fr-CA")
        : "";
      const encryptedOperation = handleEncryptValue("dashboard");
      const encryptedRole = handleEncryptValue(clientDetails.role);
      const encryptedClientId = handleEncryptValue(clientDetails.clientId);
      const encryptedProductCode = handleEncryptValue(productCode);
      const encryptedRegulation = handleEncryptValue(regulationCode);
      const encryptedStartDate = handleEncryptValue(start);
      const encryptedEndDate = handleEncryptValue(end);
      const encryptedAssetClass = handleEncryptValue(assetClass);
      console.log("assetClass: ", assetClass);
      const data = {
        client_id: encryptedClientId,
        role: encryptedRole,
        product: encryptedProductCode,
        regulation: encryptedRegulation,
        email_id: clientDetails.email,
        start_date: encryptedStartDate,
        end_date: encryptedEndDate,
        operations: encryptedOperation,
        product_data: encryptedAssetClass,
      };
      console.log("data : ", data);
      const regdataData = handleAPIEncryptValue(JSON.stringify(data));
      const url = getUrl("get-dashboard");

      const response = await get(`${url}?regdata=${regdataData}`, true);
      const { data: responseData } = response;

      if (responseData.statusCode === 200) {
        const iv = responseData.authData;
        const idToken = localStorage.getItem("userToken");
        const secretKey = idToken.slice(998, 1030);
        const body = handleDecryptResponse(
          responseData.encryptBody.toString(),
          secretKey.toString(),
          iv.toString(),
        );

        console.log("body :", body);
        const reg = productCode; // e.g. "P004"
        const pickSection = (key) => {
          const sec = body?.[key];
          if (!sec) return null;
          return sec?.[reg] ?? sec; // many sections nested under regulation code
        };
        const rowsFrom = (tbl) => {
          if (!tbl || !Array.isArray(tbl.data) || !Array.isArray(tbl.columns))
            return [];
          return tbl.data.map((rowArr) =>
            tbl.columns.reduce((o, col, i) => {
              o[col] = rowArr[i];
              return o;
            }, {}),
          );
        };
        const kpiSource = body?.regdata_kpi_data?.P004;
        const rows = kpiSource?.data ?? [];
        const columns = kpiSource?.columns ?? [];

        if (!rows.length || !columns.length) {
          setKpiData(emptyKpis);
          return;
        }

        const validIdx = columns.indexOf("Valid Record");
        const invalidIdx = columns.indexOf("Invalid Record");
        const excludedIdx = columns.indexOf("Excluded Records");

        const orderRow = getRowByType(rows, "Order");

        const validOrds = getValue(orderRow, validIdx);
        const invalidOrds = getValue(orderRow, invalidIdx);
        const excludedOrds = getValue(orderRow, excludedIdx);

        const totalOrds = validOrds + invalidOrds + excludedOrds;
        const processable = validOrds + invalidOrds;

        const validPctOfTotal = totalOrds ? (validOrds / totalOrds) * 100 : 0;
        const invalidPctOfTotal = totalOrds
          ? (invalidOrds / totalOrds) * 100
          : 0;
        const excludedPctOfTotal = totalOrds
          ? (excludedOrds / totalOrds) * 100
          : 0;

        const accuracyPct = processable ? (validOrds / processable) * 100 : 0;
        const errorPct = processable ? (invalidOrds / processable) * 100 : 0;

        setKpiData({
          totalOrds,
          validOrds,
          validPctOfTotal,
          invalidOrds,
          invalidPctOfTotal,
          excludedOrds,
          excludedPctOfTotal,
          accuracyPct,
          errorPct,
        });
        // -------------- Asset Class -----------------------
        const productRows = body?.filter_data?.P004?.data ?? [];
        const assets = productRows.map((item) => item[0]).filter(Boolean);
        setAssetClassOpts(assets);
        if (assetClass !== "all" && !assets.includes(assetClass))
          setAssetClass("all");
        // setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching KPI data:", error);
      setKpiData(emptyKpis);
    } finally {
      setKpiLoading(false);
    }
  };
  const [isDownloading, setIsDownloading] = useState(false); // <-- Download state
  const [selectedRow, setSelectedRow] = useState({});
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDetailBoxOpen, setIsDetailBoxOpen] = useState(false);
  const [hoverPos, setHoverPos] = useState(null); // {x, y}
  const [showDialog, setShowDialog] = useState(false);
  const timerRef = useRef("");
  const onCellMouseOver = (params) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const e = params.event;
      setHoverPos({ x: e.clientX, y: e.clientY });
      setShowDialog(true);
    }, 200);
  };

  const onCellMouseOut = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setShowDialog(false);
  };
  const handleDoubleClick = (params) => {
    setIsDetailBoxOpen(true);
    const clickedRow = params.data;
    if (clickedRow && Object.keys(clickedRow).length) {
      const isFieldIncludeUnderscore = Object.keys(clickedRow).some((field) =>
        field.includes("_"),
      );
      if (isFieldIncludeUnderscore) {
        let formattedData = {};
        params.api.getColumnDefs().forEach((col) => {
          formattedData[col.headerName] = clickedRow[col.field];
        });
        setSelectedRow(formattedData);
      } else {
        setSelectedRow(clickedRow);
      }
    }
  };
  const handleRefresh = useCallback(() => {
    // force a full unmount/remount:
    setRefreshCounter((count) => count + 1);
  }, [gridApi]);
  // 1) Small util
  const toYMD = (val) => {
    if (!val) return val;
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return val; // if it's already a string, leave it
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // --------------------------------------------------------------------------
  // 1. Asynchronous filter values provider
  // --------------------------------------------------------------------------
  const getAsyncFilterValues = useCallback(
    (params) => {
      const filterModel = (gridApi && gridApi.getFilterModel()) || {};
      // Create a shallow copy and remove the current field.
      const activeFilters = { ...filterModel };
      const currentField = params.colDef.field;
      delete activeFilters[currentField];
      const s = startDate?.toLocaleDateString("fr-CA") ?? "";
      const e = endDate
        ? new Date(
          new Date(endDate).setDate(endDate.getDate() + 1),
        ).toLocaleDateString("fr-CA")
        : "";
      // console.log("activeTab in filters:", activeTab);
      const queryParams = new URLSearchParams({
        client_id: clientDetails.clientId,
        product: productCode,
        regulation: regulationCode,
        filters: JSON.stringify(activeFilters),
        datefiltercolumn: "order_date_regx",
        ...(s && { start_date: s }),
        ...(e && { end_date: e }),
        email: clientDetails.email,
        f_type: activeTabRef.current, // err || records
        ...(assetClass && assetClass !== "all" && { product_data: assetClass }),
      });

      // for (const [key, value] of queryParams.entries()) {
      //   console.log(`${key}: ${value}`);
      // }
      const regDataGridData = handleAPIEncryptValue(queryParams.toString());
      const token = clientDetails.IdToken;
      fetch(`${filterUrl}?regdata=${regDataGridData}`, {
        method: "GET",
        mode: "cors",
        headers: {
          "x-api-key": clientDetails.REGDATA_FLASK_APP_ENCRYPTION_KEY,
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.json())
        .then((data) => {
          // Expect data to be an object mapping field names to arrays.
          params.success(data[currentField] || []);
        })
        .catch((error) => {
          console.error(
            `Error fetching filter values for ${currentField}:`,
            error,
          );
          params.success([]);
        });
    },
    [
      clientDetails,
      productCode,
      regulationCode,
      startDate,
      endDate,
      handleAPIEncryptValue,
      filterUrl,
      gridApi,
      assetClass,
    ],
  );

  // Formatter function for the Creation Date column
  const dateFormatter = (params) => {
    if (!params.value) return "";
    const date = new Date(params.value);
    return date.toISOString().split("T")[0]; // Returns YYYY-MM-DD
  };

  const hasSetColumns = useRef(false);

  const handleClearFilters = useCallback(() => {
    if (!gridApi) return;

    // clear all column filters
    gridApi.setFilterModel(null);

    // clear sorting (optional)
    gridApi.setSortModel?.(null);

    // clear quick filter if you ever use it (safe to keep)
    gridApi.setGridOption?.("quickFilterText", "");
    gridApi.setQuickFilter?.("");

    // refresh SSRM
    gridApi.refreshServerSide({ purge: true });

    toast.success("All filters cleared");
  }, [gridApi]);

  const runAutosize = useCallback(() => {
    if (!gridApi) return;

    // ensures grid calculates with correct width
    gridApi.doLayout?.();

    requestAnimationFrame(() => {
      try {
        const colIds = gridApi
          .getAllDisplayedColumns()
          .map((c) => c.getColId());
        gridApi.autoSizeColumns(colIds, false);
      } catch (e) {
        gridApi.autoSizeAllColumns?.();
      }
    });
  }, [gridApi]);

  useEffect(() => {
    if (!isActive) return;

    // small delay helps because tab switches use display:none -> block
    const t = setTimeout(() => {
      runAutosize();
    }, 80);

    return () => clearTimeout(t);
  }, [isActive, runAutosize]);
  // Default column definition for all columns
  const defaultColDef = useMemo(
    () => ({
      // flex: 1,
      resizable: true,
      sortable: true,
      cellStyle: { textAlign: "left" },
      floatingFilter: false,
      suppressMenu: false, // we need the column menu open for pivot
      eenableValue: true,
      enablePivot: true,
      enableRowGroup: true,
    }),
    [],
  );

  // headerTooltip: col,
  // minWidth: 270,
  // maxWidth: 500,
  const recordsColDefs = useMemo(
    () => [
      {
        field: "regx_column_2",
        headerName: "State",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "cancel"],
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "regx_column_3",
        headerName: "Version",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "cancel"],
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "record_type_regx",
        headerName: "Record Type",
        filter: "agSetColumnFilter",
        headerTooltip: "Record Type",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "cancel"],
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "source_system_regx",
        headerName: "Source System",
        filter: "agSetColumnFilter",
        headerTooltip: "Source System",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "cancel"],
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "cfi_code_regx",
        headerName: "CFI Code",
        filter: "agSetColumnFilter",
        headerTooltip: "CFI Code",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "cancel"],
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "product_regx",
        headerName: "Product",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      // {
      //   field: "order_date_regx",
      //   headerName: "Trade Date",
      //   filter: "agSetColumnFilter",
      //   headerTooltip: "Trade Date",
      //   // valueFormatter: dateFormatter,
      //   filterParams: {
      //     values: getAsyncFilterValues,
      //     // valueFormatter: dateFormatter,
      //     buttons: ["apply", "cancel"],
      //     closeOnApply: true,
      //   },
      //   cellStyle: { textAlign: "left" },
      //   resizable: true,
      // },
      {
        field: "order_date_regx",
        headerName: "Trade Date",
        filter: "agSetColumnFilter",
        valueFormatter: (params) => {
          const raw = params.value;
          if (!raw) return "";
          const d = new Date(raw);
          if (isNaN(d)) return raw;
          // console.log("raw  :", d.toISOString().split("T")[0]);
          return d.toISOString().split("T")[0];
        },
        filterParams: {
          values: getAsyncFilterValues,
          keyCreator: (p) => toYMD(p.value),
          valueFormatter: (p) => toYMD(p.value),
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
          treeList: true,
          treeListPathGetter: (rawDateValue) => {
            // console.log("rawDateValue :", rawDateValue);
            if (!rawDateValue) return ["Unknown"];
            const d = new Date(rawDateValue);
            if (isNaN(d)) return [rawDateValue];
            // console.log("d  :", d?.toLocaleDateString("fr-CA"));
            const year = String(d.getFullYear());
            const monthName = d.toLocaleString("en-US", { month: "short" });
            const day = String(d.getDate()).padStart(2, "0");
            // return d?.toLocaleDateString("fr-CA");
            return [year, monthName, day];
          },
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "name_and_deg_of_client",
        headerName: "Name and designation of the client",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Name and designation of the client",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "name_of_desig_of_the_client",
        headerName:
          "Name and designation of any relevant person acting on behalf of the client",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip:
          "Name and designation of any relevant person acting on behalf of the client",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "trader_id",
        headerName:
          "A designation to identify the trader (Trader ID) responsible within the investment firm for the investment decision",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip:
          "A designation to identify the trader (Trader ID) responsible within the investment firm for the investment decision",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "algo_id",
        headerName:
          "A designation to identify the algorithm (Algo ID) responsible within the investment firm for the investment decision",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip:
          "A designation to identify the algorithm (Algo ID) responsible within the investment firm for the investment decision",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "bs_indicator",
        headerName: "B/S indicator",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "B/S indicator",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "instrument_identification",
        headerName: "Instrument identification",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Instrument identification",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "unit_price_and_price_notation",
        headerName: "Unit price and price notation",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Unit price and price notation",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "price",
        headerName: "Price",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Price",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "price_multiplier",
        headerName: "Price multiplier",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Price multiplier",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "currency1",
        headerName: "Currency 1",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Currency 1",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "currency2",
        headerName: "Currency 2",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Currency 2",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "intial_quantity_and_quantity_notation",
        headerName: "Initial quantity and quantity notation",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Initial quantity and quantity notation",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "validity_period",
        headerName: "Validity period",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Validity period",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "type_of_the_order",
        headerName: "Type of the order",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Type of the order",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "any_othr_details_cond_particular_inst_cli",
        headerName:
          "Any other details, conditions and particular instructions from the client",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip:
          "Any other details, conditions and particular instructions from the client",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "date_and_exact_time_of_recipt",
        headerName:
          "The date and exact time of the receipt of the order or the date and exact time of when the decision to deal was made. The exact time must be measured according to the methodology prescribed under the standards on clock synchronisation under Article 50(2) of Directive 2014/65/EU",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip:
          "The date and exact time of the receipt of the order or the date and exact time of when the decision to deal was made. The exact time must be measured according to the methodology prescribed under the standards on clock synchronisation under Article 50(2) of Directive 2014/65/EU",
        filterParams: { maxNumConditions: 1 },
      },

      {
        field: "regx_column_1",
        headerName: "Unique key",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
    ],
    [clientDetails, productCode, regulationCode, gridApi, getAsyncFilterValues],
  );

  const errorColDefs = useMemo(
    () => [
      {
        field: "regx_column_2",
        headerName: "State",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "regx_column_3",
        headerName: "Version",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "record_type_regx",
        headerName: "Record Type",
        filter: "agSetColumnFilter",
        headerTooltip: "Record Type",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "cancel"],
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "source_system_regx",
        headerName: "Source System",
        filter: "agSetColumnFilter",
        headerTooltip: "Source System",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "cancel"],
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "cfi_code_regx",
        headerName: "CFI Code",
        filter: "agSetColumnFilter",
        headerTooltip: "CFI Code",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "cancel"],
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "product_regx",
        headerName: "Product",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      // {
      //   field: "order_date_regx",
      //   headerName: "Trade Date",
      //   filter: "agSetColumnFilter",
      //   headerTooltip: "Trade Date",
      //   // valueFormatter: dateFormatter,
      //   filterParams: {
      //     values: getAsyncFilterValues,
      //     // valueFormatter: dateFormatter,
      //     buttons: ["apply", "cancel"],
      //     closeOnApply: true,
      //   },
      //   cellStyle: { textAlign: "left" },
      //   resizable: true,
      // },
      {
        field: "order_date_regx",
        headerName: "Trade Date",
        filter: "agSetColumnFilter",
        valueFormatter: (params) => {
          const raw = params.value;
          if (!raw) return "";
          const d = new Date(raw);
          if (isNaN(d)) return raw;
          // console.log("raw  :", d.toISOString().split("T")[0]);
          return d.toISOString().split("T")[0];
        },
        filterParams: {
          values: getAsyncFilterValues,
          keyCreator: (p) => toYMD(p.value),
          valueFormatter: (p) => toYMD(p.value),
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
          treeList: true,
          treeListPathGetter: (rawDateValue) => {
            // console.log("rawDateValue :", rawDateValue);
            if (!rawDateValue) return ["Unknown"];
            const d = new Date(rawDateValue);
            if (isNaN(d)) return [rawDateValue];
            // console.log("d  :", d?.toLocaleDateString("fr-CA"));
            const year = String(d.getFullYear());
            const monthName = d.toLocaleString("en-US", { month: "short" });
            const day = String(d.getDate()).padStart(2, "0");
            // return d?.toLocaleDateString("fr-CA");
            return [year, monthName, day];
          },
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      // added on 23-Jan
      {
        field: "error_description",
        headerName: "Error Details",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
        },
        width: 300,
        minWidth: 300,
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "all_error_descritions",
        headerName: "Error Description Input File",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
        },
        width: 300,
        minWidth: 300,
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "input_file_column",
        headerName: "Input File Column",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      // ---
      {
        field: "name_and_deg_of_client",
        headerName: "Name and designation of the client",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Name and designation of the client",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "name_of_desig_of_the_client",
        headerName:
          "Name and designation of any relevant person acting on behalf of the client",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip:
          "Name and designation of any relevant person acting on behalf of the client",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "trader_id",
        headerName:
          "A designation to identify the trader (Trader ID) responsible within the investment firm for the investment decision",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip:
          "A designation to identify the trader (Trader ID) responsible within the investment firm for the investment decision",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "algo_id",
        headerName:
          "A designation to identify the algorithm (Algo ID) responsible within the investment firm for the investment decision",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip:
          "A designation to identify the algorithm (Algo ID) responsible within the investment firm for the investment decision",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "bs_indicator",
        headerName: "B/S indicator",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "B/S indicator",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "instrument_identification",
        headerName: "Instrument identification",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Instrument identification",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "unit_price_and_price_notation",
        headerName: "Unit price and price notation",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Unit price and price notation",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "price",
        headerName: "Price",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Price",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "price_multiplier",
        headerName: "Price multiplier",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Price multiplier",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "currency1",
        headerName: "Currency 1",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Currency 1",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "currency2",
        headerName: "Currency 2",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Currency 2",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "intial_quantity_and_quantity_notation",
        headerName: "Initial quantity and quantity notation",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Initial quantity and quantity notation",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "validity_period",
        headerName: "Validity period",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Validity period",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "type_of_the_order",
        headerName: "Type of the order",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip: "Type of the order",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "any_othr_details_cond_particular_inst_cli",
        headerName:
          "Any other details, conditions and particular instructions from the client",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip:
          "Any other details, conditions and particular instructions from the client",
        filterParams: { maxNumConditions: 1 },
      },
      {
        field: "date_and_exact_time_of_recipt",
        headerName:
          "The date and exact time of the receipt of the order or the date and exact time of when the decision to deal was made. The exact time must be measured according to the methodology prescribed under the standards on clock synchronisation under Article 50(2) of Directive 2014/65/EU",
        filter: "agTextColumnFilter",
        sortable: false,
        cellStyle: { textAlign: "left" },
        maxWidth: 500,
        headerTooltip:
          "The date and exact time of the receipt of the order or the date and exact time of when the decision to deal was made. The exact time must be measured according to the methodology prescribed under the standards on clock synchronisation under Article 50(2) of Directive 2014/65/EU",
        filterParams: { maxNumConditions: 1 },
      },

      {
        field: "regx_column_1",
        headerName: "Unique key",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
    ],
    [clientDetails, productCode, regulationCode, gridApi, getAsyncFilterValues],
  );

  const activeColDefs = useMemo(() => {
    if (activeTab === "err") return errorColDefs;
    return recordsColDefs;
  }, [activeTab, errorColDefs, recordsColDefs]);

  const drilldownKey = navigationDetails?.time || "";
  const gridKey = useMemo(() => {
    const sd = startDate?.toISOString().slice(0, 10) ?? "";
    const ed = endDate?.toISOString().slice(0, 10) ?? "";
    // console.log(sd);
    // console.log(ed);
    return `${sd}_${ed}_${activeTab}_${refreshCounter}_${drilldownKey}_${assetClass}`;
  }, [startDate, endDate, refreshCounter, assetClass, activeTab, drilldownKey]);

  // Default column definition for all columns

  // --------------------------------------------------------------------------
  // 3. Server-side datasource: fetch row data using the grid’s filter model.
  // --------------------------------------------------------------------------
  const onGridReady = useCallback(
    (params) => {
      setGridApi(params.api);
      setIsLoading(true);
      params.api.closeToolPanel();
      const datasource = {
        getRows: async (params) => {
          const {
            startRow,
            endRow,
            filterModel,
            sortModel,
            pivotMode,
            pivotCols, // ← AG Grid’s list of pivot columns
            valueCols, // ← AG Grid’s list of value columns
            rowGroupCols,
          } = params.request;
          const limit = params.api.paginationGetPageSize();
          const page = Math.floor(startRow / limit) + 1;
          const s = startDate?.toLocaleDateString("fr-CA") ?? "";
          const e = endDate
            ? new Date(
              new Date(endDate).setDate(endDate.getDate() + 1),
            ).toLocaleDateString("fr-CA")
            : "";
          // Process filters into the format your API expects.
          const filters = {};
          Object.keys(filterModel).forEach((colId) => {
            const filterObj = filterModel[colId];
            if (filterObj.values) {
              filters[colId] = {
                type: filterObj.filterType,
                values: filterObj.values,
              };
            } else if (filterObj.conditions) {
              filters[colId] = {
                operator: filterObj.operator,
                conditions: filterObj.conditions.map((cond) => ({
                  type: cond.type,
                  value: cond.filter,
                })),
              };
            } else {
              filters[colId] = {
                type: filterObj.type,
                value: filterObj.filter,
              };
            }
          });
          const drilldownFiltersCurrent = drilldownFiltersRef.current;
          if (drilldownFiltersCurrent) {
            Object.entries(drilldownFiltersCurrent).forEach(
              ([field, filter]) => {
                filters[field] = {
                  type: "set",
                  values: filter.values,
                };
              },
            );
          }
          let sort_by = "";
          let order = "";
          if (sortModel && sortModel.length > 0) {
            sort_by = sortModel[0].colId;
            order = sortModel[0].sort === "asc" ? "ascending" : "descending";
          }
          // console.log("s :", s, " e :", e);

          const queryParams = new URLSearchParams({
            startRow,
            endRow,
            client_id: clientDetails.clientId,
            product: productCode,
            regulation: regulationCode,
            filters: JSON.stringify(filters),
            fType: activeTab,
            //new added params
            ...(s && { start_date: s }),
            ...(e && { end_date: e }),
            email: clientDetails.email,
            datefiltercolumn: "order_date_regx",
            f_type: activeTabRef.current,
            ...(assetClass &&
              assetClass !== "all" && { product_data: assetClass }),
          });
          // for (const [key, value] of queryParams.entries()) {
          //   console.log(`${key}: ${value}`);
          // }
          // Append sort parameters if sorting is applied.
          if (sort_by) {
            queryParams.append("sort_by", sort_by);
            queryParams.append("order", order);
          }
          queryParams.append("pivotMode", pivotMode);

          const pivotFields = pivotCols.map((p) => p.field).join(","); // Columns
          const groupFields = rowGroupCols.map((r) => r.field).join(","); // Rows
          const aggField = valueCols[0]?.field || "";
          const aggFunc = valueCols[0]?.aggFunc || "";

          queryParams.append("pivotCols", pivotFields); // can be empty
          queryParams.append("rowgroupCols", groupFields); // can be empty
          queryParams.append("aggCol", aggField); // can be empty
          queryParams.append("aggFunc", aggFunc); // can be empty

          const regDataGridData = handleAPIEncryptValue(queryParams.toString());

          try {
            const gridUrl = getUrl("get-flask-data");
            const token = clientDetails.IdToken;
            const response = await fetch(
              `${gridUrl}?regdata=${regDataGridData}`,
              {
                method: "GET",
                mode: "cors",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                  "x-api-key": clientDetails.REGDATA_FLASK_APP_ENCRYPTION_KEY,
                },
              },
            );
            // console.log(response, '--response');
            const statusCode = response.status;
            if (statusCode === 403) {
              toast.error("Session expired. Please login again");
              tokenExpire(statusCode, navigate);
              return;
            }
            if (!response.ok) {
              throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            // console.log("data :", data);
            if (data && data.rowData) {
              if (data.rowData.length === 0) {
                params.success({
                  rowCount: 0,
                  rowData: [],
                });
                params.api.showNoRowsOverlay();
              } else {
                params.api.hideOverlay();
                params.success({
                  rowData: data.rowData,
                  rowCount: data.rowCount,
                });
              }
            } else {
              params.fail();
            }
          } catch (error) {
            console.error("Error fetching data:", error);
            console.log(error);
            params.fail();
          } finally {
            setIsLoading(false);
          }
        },
      };
      params.api.setGridOption("serverSideDatasource", datasource);
      setTimeout(() => {
        params.api.autoSizeAllColumns();
      }, 200);
    },
    [
      clientDetails,
      productCode,
      regulationCode,
      startDate,
      endDate,
      activeTab,
      drilldownFilters,
      assetClass,
    ],
  );

  // --------------------------------------------------------------------------
  // 4. onFilterChanged: Refresh asynchronous filter values for the columns using set filters
  // --------------------------------------------------------------------------
  const onFilterChanged = useCallback(() => {
    if (filterChangedTimeout.current) {
      clearTimeout(filterChangedTimeout.current);
    }
    filterChangedTimeout.current = setTimeout(() => {
      if (!gridApi) return;
      const currentModel = gridApi.getFilterModel();
      const colDefs = gridApi.getColumnDefs() || [];
      colDefs.forEach((colDef) => {
        if (colDef.filter === "agSetColumnFilter") {
          // Determine if the filter for this column is active.
          const isActive = !!currentModel[colDef.field];
          // Only refresh if the column was inactive previously and remains inactive.
          if (!isActive && !prevAsyncActiveState.current[colDef.field]) {
            gridApi
              .getColumnFilterInstance(colDef.field)
              .then((filterInstance) => {
                if (
                  filterInstance &&
                  typeof filterInstance.refreshFilterValues === "function"
                ) {
                  try {
                    filterInstance.refreshFilterValues();
                  } catch (e) {
                    console.warn(
                      `Refresh error for ${colDef.field}: ${e.message}`,
                    );
                  }
                }
              });
          }
          // Update the previous active state.
          prevAsyncActiveState.current[colDef.field] = isActive;
        }
      });
    }, 300);
  }, [gridApi]);
  // --------------------------------------------------------------------------
  // 5. CSV export – downloads the CSV based on the current filter model.
  // --------------------------------------------------------------------------
  const downloadCSV = async () => {
    if (!gridApi) {
      console.error("gridApi is not available");
      return;
    }

    const MAX_ROWS = 500000;
    // console.log(totalRowCount, '=TotalRC');

    if (totalRowCount > MAX_ROWS) {
      setShowContactSupport(true);
      setShowSupportDialog(true); // 👈 open the dialog instead of alert
      return;
    }

    setIsDownloading(true);
    const allColIds = gridApi
      .getAllDisplayedColumns()
      .map((col) => col.getColId())
      .filter((id) => id !== "ag-Grid-SelectionColumn");

    const defaultVisibleColumns = activeColDefs
      .filter((colDef) => colDef.field && colDef.hide !== true)
      .map((colDef) => colDef.field);

    const isDefaultColumns =
      allColIds.length === defaultVisibleColumns.length &&
      allColIds.every((col) => defaultVisibleColumns.includes(col));

    const selectedColumns = isDefaultColumns ? [] : allColIds;

    const filterModel = gridApi.getFilterModel();
    const token = clientDetails.IdToken;
    const s = startDate?.toLocaleDateString("fr-CA") ?? "";
    const e = endDate
      ? new Date(
        new Date(endDate).setDate(endDate.getDate() + 1),
      ).toLocaleDateString("fr-CA")
      : "";
    const queryParams = new URLSearchParams({
      client_id: clientDetails.clientId,
      product: productCode,
      regulation: regulationCode,
      email: clientDetails.email,
      ...(s && { start_date: s }),
      ...(e && { end_date: e }),
      datefiltercolumn: "order_date_regx",
      f_type: activeTabRef.current, // err || records
      ...(assetClass && assetClass !== "all" && { product_data: assetClass }),
    });
    // for (const [key, value] of queryParams.entries()) {
    //   console.log(`${key}: ${value}`);
    // }
    queryParams.append("filters", JSON.stringify(filterModel));
    queryParams.append("columns", JSON.stringify(selectedColumns));

    const regDataGridData = handleAPIEncryptValue(queryParams.toString());

    try {
      const response = await fetch(
        `${downloadUrl}?regdata=${regDataGridData}`,
        {
          method: "GET",
          mode: "cors",
          headers: {
            "x-api-key": clientDetails.REGDATA_FLASK_APP_ENCRYPTION_KEY,
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }

      const blob = await response.blob();
      const now = new Date();
      const pad = (num) => String(num).padStart(2, "0");
      const fileName = `annex_orders_${now.getFullYear()}${pad(
        now.getMonth() + 1,
      )}${pad(now.getDate())}_${pad(now.getHours())}${pad(
        now.getMinutes(),
      )}${pad(now.getSeconds())}.csv`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
    } catch (error) {
      console.error("Error downloading CSV:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  // --------------------------------------------------------------------------
  // 6. Check regulation accessibility based on client details.
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (gridApi && isActive) {
      gridApi.autoSizeAllColumns();
    }
  }, [gridApi, isActive]);

  const onFirstDataRendered = useCallback(
    (params) => {
      if (!isActive) return;
      params.api.autoSizeAllColumns();
    },
    [isActive],
  );

  const onModelUpdated = useCallback(
    (params) => {
      if (!isActive) return;
      params.api.autoSizeAllColumns();
    },
    [isActive],
  );
  useEffect(() => {
    if (
      productCode in clientDetails.dataAccess &&
      clientDetails.dataAccess.P004.includes(regulationCode)
    ) {
      setIsRegulationAccessible(true);
    } else {
      setIsRegulationAccessible(false);
    }
  }, [regulationCode, clientDetails, productCode]);

  // set drill down tab
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
      activeTabRef.current = initialTab;
    }
  }, [initialTab]);

  // Drill-down filters are applied directly in the grid payload (no UI filter)
  useEffect(() => {
    drilldownFiltersRef.current = drilldownFilters;
    if (gridApi) {
      gridApi.refreshServerSide({ purge: true });
    }
  }, [drilldownFilters, gridApi]);

  // Fetch KPI data when component mounts or dates change
  useEffect(() => {
    if (isRegulationAccessible && startDate && endDate && isActive) {
      fetchKpiData();
    }
  }, [startDate, endDate, isRegulationAccessible, assetClass, isActive]);

  // Sync assetClassOptions from sharedFilters when analytics populates them
  useEffect(() => {
    if (sharedFilters?.assetClassOpts?.length) {
      setAssetClassOpts(sharedFilters.assetClassOpts);
    }
  }, [sharedFilters?.assetClassOpts]);

  // KPI Value wrapper component
  const KpiValue = ({ children, className = "text-start" }) => {
    if (kpiLoading) {
      return (
        <div className={className}>
          <CircularProgress className={className} size={16} />
        </div>
      );
    }
    return <>{children}</>;
  };

  return (
    <main
      className={`${isFullscreen ? "" : "middle_section"} dashboard_middle`}
    >
      <div className="container-fluid p-0">
        {/* KPIs Section */}
        {isRegulationAccessible ? (
          <>
            <div className="grid-kpi-wrapper">
              <div className="d-flex justify-content-between align-items-end mb-3 flex-wrap gap-3">
                <div className="d-flex align-items-end gap-3 flex-wrap">
                  <div>
                    <CustomDatepicker
                      startDate={startDate}
                      endDate={endDate}
                      onChange={(newDateRange) => {
                        setDateRange(newDateRange);
                        if (newDateRange[0] && newDateRange[1]) {
                          setTimeout(() => fetchKpiData(), 100);
                        }
                      }}
                      showWrapperStyles={false}
                    />
                  </div>

                  <FormControl
                    sx={{
                      minWidth: 180,
                      width: "fit-content",
                      margin: 0,
                    }}
                    size="small"
                  >
                    <label
                      style={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#777f86",
                        marginBottom: "4px",
                        display: "block",
                      }}
                    >
                      Asset Class
                    </label>
                    <div className="filter">
                      <select
                        value={assetClass}
                        onChange={(e) => {
                          const next = e.target.value;
                          setAssetClass(next);

                          if (setSharedFilters) {
                            setSharedFilters((prev) => ({
                              ...(prev || {}),
                              assetClass: next,
                            }));
                          }
                        }}
                      >
                        <option value="all">All Asset Classes</option>
                        {assetClassOpts.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                    </div>
                  </FormControl>
                </div>

                {/* Right Section */}
                <GridGroupIcons
                  onRefresh={() => {
                    handleRefresh();
                    fetchKpiData();
                  }}
                  fullScreenIcon={false}
                  iconsDisabled={isLoading || kpiLoading}
                  downloadIcon={false}
                />
              </div>
              <div className="kpis">
                {/* TOTAL */}
                <div className="kpi violet">
                  <div className="kpi-single-value">
                    <div>
                      <div className="kpi-title">Total Records</div>
                      <div className="kpi-value">
                        <KpiValue>{fmt(kpiData.totalOrds)}</KpiValue>
                      </div>
                    </div>
                  </div>
                  <div className="kpi-foot">
                    in current view
                    <span onClick={() => openBasicKpiModal("totalRecords")}>
                      <FaInfoCircle className="kpi-info-icon" />
                    </span>
                  </div>
                </div>

                {/* VALID */}
                <div className="kpi green">
                  <div className="kpi-single-value">
                    <div>
                      <div className="kpi-title">Processed Records</div>
                      <div className="kpi-value">
                        <KpiValue>{fmt(kpiData.validOrds)}</KpiValue>
                      </div>
                    </div>
                  </div>
                  <div className="kpi-foot">
                    {kpiData.validPctOfTotal.toFixed(2)}% of total
                    <span onClick={() => openBasicKpiModal("processedRecords")}>
                      <FaInfoCircle className="kpi-info-icon" />
                    </span>
                  </div>
                </div>

                {/* INVALID */}
                <div className="kpi red">
                  <div className="kpi-single-value">
                    <div>
                      <div className="kpi-title">Exceptions</div>
                      <div className="kpi-value">
                        <KpiValue>{fmt(kpiData.invalidOrds)}</KpiValue>
                      </div>
                    </div>
                  </div>
                  <div className="kpi-foot">
                    {kpiData.invalidPctOfTotal.toFixed(2)}% of total
                    <span
                      onClick={() => openBasicKpiModal("exceptionsRecords")}
                    >
                      <FaInfoCircle className="kpi-info-icon" />
                    </span>
                  </div>
                </div>

                {/* EXCLUDED */}
                <div className="kpi amber">
                  <div className="kpi-single-value">
                    <div>
                      <div className="kpi-title">Excluded Records</div>
                      <div className="kpi-value">
                        <KpiValue>{fmt(kpiData.excludedOrds)}</KpiValue>
                      </div>
                    </div>
                  </div>
                  <div className="kpi-foot">
                    {kpiData.excludedPctOfTotal.toFixed(2)}% of total
                    <span onClick={() => openBasicKpiModal("excludedRecords")}>
                      <FaInfoCircle className="kpi-info-icon" />
                    </span>
                  </div>
                </div>

                {/* ACCURACY */}
                <div className="kpi green">
                  <div className="kpi-single-value">
                    <div>
                      <div className="kpi-title">Accuracy %</div>
                      <div className="kpi-value">
                        <KpiValue>{kpiData.accuracyPct.toFixed(2)}%</KpiValue>
                      </div>
                    </div>
                  </div>
                  <div className="kpi-foot">
                    <span
                      onClick={() => openBasicKpiModal("accuracyPercentage")}
                    >
                      <FaInfoCircle className="kpi-info-icon" />
                    </span>
                  </div>
                </div>

                {/* ERROR */}
                <div className="kpi red">
                  <div className="kpi-single-value">
                    <div>
                      <div className="kpi-title">Error %</div>
                      <div className="kpi-value">
                        <KpiValue>{kpiData.errorPct.toFixed(2)}%</KpiValue>
                      </div>
                    </div>
                  </div>
                  <div className="kpi-foot">
                    <span onClick={() => openBasicKpiModal("errorPercentage")}>
                      <FaInfoCircle className="kpi-info-icon" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div
              className={`dashboard_inner pt-0 ${isFullscreen ? "fullscreen-grid" : "mt-3"
                }`}
            >
              <div
                className={`rx_table_wrapper table-responsive padding-fix ${activeTab == "err" ? "red-grid" : ""
                  }`}
              >
                <div className="d-flex justify-content-between align-items-center grid-icon-wrapper">
                  <DynamicTabSwitcher
                    onTabChange={handleTabChange}
                    tabMap={TAB_MAP}
                    activeTab={activeTab}
                    wrapperClassName="pt-0 pb-0"
                    disabledTabs={[]}
                  />
                  <div className="d-flex align-items-center gap-2">
                    <Tooltip
                      title="Clear applied filters"
                      arrow
                      placement="top"
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleClearFilters}
                          disabled={isLoading || !gridApi}
                          sx={{
                            border: "1px solid #cbd5e1",
                            borderRadius: "8px",
                            height: 36,
                            width: 36,
                          }}
                        >
                          <FilterAltOffIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <GridGroupIcons
                      refreshIcon={false}
                      isFullscreen={isFullscreen}
                      iconsDisabled={isLoading || kpiLoading}
                      onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
                      onDownloadCSV={downloadCSV}
                      isDownloading={isDownloading}
                    // borderRadius={10}
                    />
                  </div>
                </div>
                <div className={`container-fluid p-0`}>
                  <div
                    className="ag-theme-alpine ag-theme-quartz"
                    style={{
                      height: isFullscreen ? "calc(100vh - 60px)" : "80vh",
                      width: "100%",
                    }}
                  >
                    <AgGridReact
                      ref={gridRef}
                      key={gridKey}
                      theme="legacy"
                      columnDefs={activeColDefs}
                      defaultColDef={defaultColDef}
                      rowModelType="serverSide"
                      pagination={true}
                      paginationPageSize={20}
                      paginationPageSizeSelector={[10, 20, 50, 100]}
                      cacheBlockSize={10}
                      onFirstDataRendered={onFirstDataRendered}
                      onModelUpdated={onModelUpdated}
                      onGridReady={onGridReady}
                      onFilterChanged={onFilterChanged}
                      onCellMouseOver={onCellMouseOver}
                      onCellMouseOut={onCellMouseOut}
                      onRowDoubleClicked={handleDoubleClick}
                      cellSelection={true}
                      enableCharts
                      // pivotMode={false}
                      // pivotPanelShow="always"
                      // sideBar="columns"
                      // serverSidePivotResultFieldSeparator="."
                      noRowsOverlayComponent={GridDataNotFound}
                    />
                  </div>
                </div>
              </div>
              {/* <div className="d-flex justify-content-end mt-4">
              <button
                className="dash_btn"
                type="submit"
                onClick={downloadCSV}
                disabled={isDownloading}
                style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
              >
                {isDownloading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Download CSV"}
              </button>
            </div> */}
              <GridRowDetailsExpand
                showDialog={showDialog}
                selectedRow={selectedRow}
                hoverPos={hoverPos}
                isErrGrid={activeTab === "err"}
                isDetailBoxOpen={isDetailBoxOpen}
                setIsDetailBoxOpen={setIsDetailBoxOpen}
              />
            </div>
          </>
        ) : (
          <NotSubscribedMessageComponent />
        )}
      </div>
      {/* ✅ Place your dialog here */}
      <Dialog
        sx={{ zIndex: 99999999 }}
        open={showSupportDialog}
        disableScrollLock
        onClose={() => setShowSupportDialog(false)}
        fullWidth
        maxWidth="md"
      >
        {showTable ? (
          <>
            <DialogTitle>Requested Files</DialogTitle>
            <DialogContent>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>File Name</TableCell>
                      <TableCell>Requested On</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { name: "rts24_jan_data.csv", date: "2025-01-10" },
                      { name: "rts24_feb_data.csv", date: "2025-02-14" },
                      { name: "rts24_mar_data.csv", date: "2025-03-05" },
                      { name: "rts24_jan_data.csv", date: "2025-01-10" },
                      { name: "rts24_feb_data.csv", date: "2025-02-14" },
                      { name: "rts24_mar_data.csv", date: "2025-03-05" },
                      { name: "rts24_jan_data.csv", date: "2025-01-10" },
                      { name: "rts24_feb_data.csv", date: "2025-02-14" },
                      { name: "rts24_mar_data.csv", date: "2025-03-05" },
                      { name: "rts24_jan_data.csv", date: "2025-01-10" },
                      { name: "rts24_feb_data.csv", date: "2025-02-14" },
                      { name: "rts24_mar_data.csv", date: "2025-03-05" },
                      { name: "rts24_jan_data.csv", date: "2025-01-10" },
                      { name: "rts24_feb_data.csv", date: "2025-02-14" },
                      { name: "rts24_mar_data.csv", date: "2025-03-05" },
                    ].map((file, index) => (
                      <TableRow key={index}>
                        <TableCell>{file.name}</TableCell>
                        <TableCell>{file.date}</TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            onClick={() =>
                              toast.info(`Downloading ${file.name}...`)
                            }
                          >
                            Download
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowTable(false)} color="primary">
                Back
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle fontSize={22} fontWeight={500}>
              Download Limit Exceeded
            </DialogTitle>
            <DialogContent>
              <Typography mb={2} fontSize={16}>
                Download limit is 5,00,000 rows. Please contact support for data
                more than 5,00,000 rows.
              </Typography>
              <Typography mb={0} fontSize={14}>
                Contact Us:{" "}
                <p style={{ color: "blue", display: "inline" }}>
                  {" "}
                  support.reg-x.co.uk{" "}
                </p>
              </Typography>
              {/* <Box sx={{ px: 2, pt: 1 }}>
                      <Slider
                        value={rowRange}
                        onChange={(e, newValue) => setRowRange(newValue)}
                        valueLabelDisplay="auto"
                        min={rowRangeLimits.min}
                        max={rowRangeLimits.max}
                        step={10000}
                        marks={[
                          { value: rowRangeLimits.min, label: `${rowRangeLimits.min}` },
                          { value: rowRangeLimits.max, label: `${rowRangeLimits.max}` },
                        ]}
                      />
      
                      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                        <TextField
                          label="Min Rows"
                          type="number"
                          value={rowRange[0]}
                          onChange={(e) => {
                            const newMin = parseInt(e.target.value, 10);
                            if (!isNaN(newMin)) {
                              // Ensure min doesn't exceed max
                              setRowRange(([_, max]) => [Math.min(newMin, max), max]);
                            }
                          }}
                          inputProps={{ min: rowRangeLimits.min, max: rowRangeLimits.max }}
                          fullWidth
                        />
                        <TextField
                          label="Max Rows"
                          type="number"
                          value={rowRange[1]}
                          onChange={(e) => {
                            const newMax = parseInt(e.target.value, 10);
                            if (!isNaN(newMax)) {
                              // Ensure max doesn't fall below min
                              setRowRange(([min, _]) => [min, Math.max(newMax, min)]);
                            }
                          }}
                          inputProps={{ min: rowRangeLimits.min, max: rowRangeLimits.max }}
                          fullWidth
                        />
                      </Box>
      
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Selected Range: {rowRange[0].toLocaleString()} - {rowRange[1].toLocaleString()} rows
                      </Typography>
                    </Box> */}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setShowSupportDialog(false);
                }}
                color="primary"
              >
                Cancel
              </Button>
              {/* <Button
                      onClick={() => {
                        handleContactSupport();
                        setShowSupportDialog(false);
                      }}
                      color="primary"
                      variant="contained"
                    >
                      SEND REQUEST
                    </Button>
                    <Button
                      onClick={() => setShowTable(true)}
                      color="secondary"
                      variant="outlined"
                    >
                      Show Requested Files
                    </Button> */}
            </DialogActions>
          </>
        )}
      </Dialog>
      <BasicKPICalculation
        kpiKey={activeBasicKpiKey}
        regulationCode={regulationCode}
        kpiData={kpiData}
        onClose={closeBasicKpiModal}
      />
    </main>
  );
}

export default EURecordKeepingAnnexIVOrdersComponent;
